import { Box, IconButton, Menu, MenuItem } from '@mui/material'
import { Dotsmenu } from '@e3dc-react/icons'
import React, { useCallback, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { CompanyUserData } from 'src/State/User/UserModel'
import { getFullName } from 'src/Libs/utils/string-functions'
import { ChangeReason } from 'src/Libs/form'

interface Props {
  item: CompanyUserData
  onRemove: (value: CompanyUserData, deletionReason: ChangeReason) => Promise<void>
  onChangeManager: (value: CompanyUserData, changeManagerReason: ChangeReason, removeOldManager: boolean) => void
  companyName: string
  managerId?: number
}

/**
 * Providing all the possible actions for a linked user invite to a company.
 *
 * Used as the functional additions to the users table in the company profile view.
 *
 * @param item
 * @param onRemove
 * @param onChangeManager
 * @param companyName
 * @param managerId
 * @constructor
 */
const CompanySettingsUserActionMenu: React.FC<Props> = ({ item, onRemove, onChangeManager, companyName, managerId }) => {
  const { t } = useCustomTranslation()
  const { openAssignManagerDialog, openReasonDialog } = useModals()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const closeMenu = (): void => {
    setAnchorEl(null)
  }

  const processRemove = useCallback(
    (deletionReason: ChangeReason): Promise<void> => {
      return onRemove(item, deletionReason)
    },
    [onRemove, item]
  )

  const handleClose = useCallback((): void => {
    closeMenu()
  }, [])

  const processChangeManager = useCallback(
    (changeManagerReason: ChangeReason, removeOldManager: boolean): void => {
      onChangeManager(item, changeManagerReason, removeOldManager)
    },
    [onChangeManager, item]
  )

  const handleRemoveDialog = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      openReasonDialog({
        onSubmit: processRemove,
        title: t('user.company.installerDeletion.title'),
        description: t('user.company.installerDeletion.text', { user: `${item.Vorname} ${item.Name}` }),
      })
      closeMenu()
    },
    [item.Name, item.Vorname, openReasonDialog, processRemove, t]
  )

  const handleAssignManagerDialog = useCallback((): void => {
    openAssignManagerDialog({
      onConfirm: processChangeManager,
      companyName,
      managerId,
      selectedUser: getFullName(item.Vorname, item.Name),
    })
    closeMenu()
  }, [openAssignManagerDialog, processChangeManager, companyName, managerId, item.Vorname, item.Name])

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <Dotsmenu />
      </IconButton>
      <Menu id="action-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleRemoveDialog}>{t('user.company.installerDeletion.menu.remove')}</MenuItem>
        <MenuItem onClick={handleAssignManagerDialog}>{t('user.company.installerDeletion.menu.assign-manager')}</MenuItem>
      </Menu>
    </Box>
  )
}

export default CompanySettingsUserActionMenu
